// src/components/settings/ChecklistTemplateEditor.js
// CHECKLISTS SEAM 6 VERIFIER: chklstbuild-6c1e9b42
//
// The checklist editor, opened in place inside Settings › Checklists (ChecklistSettings).
// Saves through saveChecklistTemplate with the server's version guard
// (functions/utils/checklists.js runSaveTemplate): expectedVersion 0 creates, otherwise the
// save is refused if anyone saved since this edit started.
//
// DRAFT MODEL (same idea as PipelineSettings):
//   - `base` is the saved checklist the edits started from, `version` its version; `draft` is
//     the edit. Unsaved = draft differs from base (checklistTemplateDraft.isDirty).
//   - The template's document is watched live, but a newer saved version is only RECORDED: it is
//     never swapped in underneath the person (Round 3 fix — a silent swap took on the newer
//     version number, so a stale edit saved past the server's guard). Once there are unsaved
//     edits on an older version, a banner says someone else changed it — BEFORE Save — and Save
//     sends the version the edit started from, which the server refuses.
//   - A refused save keeps the edits on screen and offers: load their version (asks first), or
//     save mine as a new checklist. Edits are never thrown away silently.
//   - Leaving with unsaved changes asks first (browser refresh/close, sidebar links, other
//     Settings tabs, "‹ All checklists", Cancel). The browser Back button can't be intercepted.
//   - Validation mirrors the server (checklistTemplateDraft.validateDraft, tested against the
//     server's own validator), shown next to the problem after the first Save attempt.
//
// LAYOUT (Round 2 rework): each section card owns its questions and its own "+ Add question"
// (the 10 types open inline; the new question goes at the END of that section). ↑ ↓ move only
// within a section; "Move to section" moves a question to the end of another section. The
// right panel holds only the checklist-level settings. Round 3: "Duplicate section" (copy just
// below, new ids) and a per-question type menu (checklistTemplateDraft.changeQuestionType).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import {
    ChevronLeft, Lock, ArrowUp, ArrowDown, Trash2, Plus, X, Copy, AlertTriangle, CheckCircle, Info,
    Type, AlignLeft, ThumbsUp, CircleDot, CheckSquare, Hash, Camera, Calendar, PenLine, RefreshCw, ListChecks,
} from 'lucide-react';
import { db } from '../../firebase/config';
import { saveChecklistTemplate } from '../../utils/checklistClient';
import {
    CHECKLIST_LIMITS, QUESTION_TYPE_ORDER, QUESTION_TYPE_LABELS, QUESTION_TYPE_HINTS, OPTION_TYPES, canAllowNote, canAllowPhotos,
    duplicateSection, duplicateSectionBlocked, changeQuestionType, questionTypeChangeLoss,
    draftFromTemplate, toSaveInput, isDirty, validateDraft, countQuestions, questionSummary, totalItems,
    suffixedName, addSection, updateSection, removeSection, addQuestion, updateQuestion, removeQuestion,
    moveQuestion, canMoveQuestion, moveQuestionToSection, sectionLabel, isStaleTemplateError,
} from '../../utils/checklistTemplateDraft';

const TYPE_ICONS = {
    short: Type, long: AlignLeft, yesno: ThumbsUp, choice: CircleDot, checkbox: CheckSquare, multi: ListChecks, // chklstmulti-3e9b7c15
    number: Hash, photos: Camera, date: Calendar, signature: PenLine, info: Info,
};

const LEAVE_WARNING = 'You have unsaved changes to this checklist. Leave without saving?';
const STALE_TEXT = 'Someone else changed this checklist while you were editing. Reload to see their changes.';
const INPUT = 'w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30';

const FieldError = ({ messages }) => (messages && messages.length ? (
    <div data-cl-error className="mt-1 space-y-0.5">
        {messages.map((m) => <p key={m} className="text-xs text-red-600">{m}</p>)}
    </div>
) : null);

export default function ChecklistTemplateEditor({ orgId, templateId: initialTemplateId, seedDraft, seedDirty, onExit }) {
    const containerRef = useRef(null);
    const [templateId, setTemplateId] = useState(initialTemplateId || null);
    const [draft, setDraft] = useState(seedDraft || null);
    const [base, setBase] = useState(seedDraft && !seedDirty ? seedDraft : null);
    const [version, setVersion] = useState(seedDraft ? 0 : null); // null = still loading
    const [liveVersion, setLiveVersion] = useState(null);
    const [missing, setMissing] = useState(false);
    const [pickerFor, setPickerFor] = useState(null); // section id whose question-type picker is open
    const [showErrors, setShowErrors] = useState(false);
    const [saving, setSaving] = useState(false);
    const [refused, setRefused] = useState(false);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');

    const dirty = !!draft && isDirty(base, draft);
    const latestRef = useRef(null);  // the newest saved version seen live (a draft)
    const loadedRef = useRef(!!seedDraft);
    const focusRef = useRef(null);   // question id to scroll to + focus after it renders

    const adopt = (d) => {
        setDraft(d);
        setBase(d);
        setVersion(d.version);
    };

    // ── live saved version ──────────────────────────────────────────────────
    useEffect(() => {
        if (!orgId || !templateId) return undefined;
        setLiveVersion(null);
        return onSnapshot(doc(db, `organizations/${orgId}/checklistTemplates/${templateId}`), (snap) => {
            if (!snap.exists()) { setMissing(true); return; }
            setMissing(false);
            const d = draftFromTemplate(templateId, snap.data());
            // Every later snapshot is only RECORDED. base / version change only when THIS editor
            // saves or the person picks "Reload their version" — so a save always sends the version
            // the edit started from, and the server's guard can refuse a stale one.
            latestRef.current = d;
            setLiveVersion(d.version);
            if (!loadedRef.current) {
                loadedRef.current = true;
                adopt(d);
            }
        }, (err) => {
            console.error('ChecklistTemplateEditor: could not load the checklist', err);
            setError('Could not load this checklist. Please refresh the page.');
        });
    }, [orgId, templateId]);

    // ── leave guard ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!dirty) return undefined;
        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        // Capture phase on document runs before React's own click handling, so cancelling
        // here stops sidebar NavLinks and Settings tab buttons (same as PipelineSettings).
        const onClickCapture = (e) => {
            if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            const el = e.target && e.target.closest ? e.target.closest('a[href], [data-settings-tab]') : null;
            if (!el) return;
            if (containerRef.current && containerRef.current.contains(el)) return;
            if (el.getAttribute('data-settings-tab') === 'checklists') return;
            if (el.tagName === 'A' && (el.getAttribute('target') === '_blank' || el.hasAttribute('download'))) return;
            if (!window.confirm(LEAVE_WARNING)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClickCapture, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClickCapture, true);
        };
    }, [dirty]);

    // Scroll to and focus a question that was just added.
    useEffect(() => {
        const id = focusRef.current;
        if (!id) return;
        focusRef.current = null;
        const el = document.getElementById(`clq-${id}`);
        if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            const input = el.querySelector('input, textarea');
            if (input) input.focus({ preventScroll: true });
        }
    }, [draft]);

    const validation = useMemo(() => (draft ? validateDraft(draft) : null), [draft]);
    const counts = useMemo(() => countQuestions(draft?.sections), [draft]);

    // ── editing ─────────────────────────────────────────────────────────────
    const edit = (fn) => {
        setDraft((d) => fn(d));
        setNotice('');
        setError('');
    };

    const leave = () => {
        if (dirty && !window.confirm(LEAVE_WARNING)) return;
        onExit();
    };

    const sections = draft?.sections || [];
    const itemCount = draft ? totalItems(draft) : 0;
    const atQuestionCap = itemCount >= CHECKLIST_LIMITS.questions;
    const atSectionCap = sections.length >= CHECKLIST_LIMITS.sections;

    // Builds from the current draft directly (not an updater) because it needs the new id
    // straight away, to focus the new question's text box. Adds at the END of the section.
    const onAddQuestion = (sectionId, type) => {
        if (atQuestionCap) return;
        const r = addQuestion(draft, sectionId, type);
        focusRef.current = r.id;
        setPickerFor(null);
        edit(() => r.draft);
    };

    const onAddSection = () => {
        if (atSectionCap) return;
        edit((d) => addSection(d).draft);
    };

    const onDuplicateSection = (s) => {
        if (duplicateSectionBlocked(draft, s.id)) return;
        edit((d) => duplicateSection(d, s.id).draft);
    };

    // Switching type asks first only when typed choices or a unit would be thrown away.
    const onChangeType = (q, type) => {
        const loss = questionTypeChangeLoss(q, type);
        if (loss === 'choices' && !window.confirm('Switching removes the choices you typed.')) return;
        if (loss === 'unit' && !window.confirm('Switching removes the unit you typed.')) return;
        edit((d) => changeQuestionType(d, q.id, type));
    };

    const onRemoveSection = (s) => {
        const n = s.questions.length;
        if (n > 0 && !window.confirm(`Remove this section and its ${n} question${n === 1 ? '' : 's'}?`)) return;
        edit((d) => removeSection(d, s.id));
    };

    // ── saving ──────────────────────────────────────────────────────────────
    const scrollToFirstError = () => {
        requestAnimationFrame(() => {
            const el = containerRef.current && containerRef.current.querySelector('[data-cl-error]');
            if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        });
    };

    const handleSave = async () => {
        setShowErrors(true);
        setNotice('');
        setError('');
        if (!validation.ok) { scrollToFirstError(); return; }
        const sent = draft;
        const wasExisting = !!templateId;
        setSaving(true);
        try {
            const res = await saveChecklistTemplate({
                ...toSaveInput(sent),
                expectedVersion: version,
                ...(templateId ? { templateId } : {}),
            });
            loadedRef.current = true;
            if (!wasExisting) setTemplateId(res.templateId);
            setVersion(res.version);
            setBase(sent);
            setRefused(false);
            setShowErrors(false);
            setNotice(wasExisting ? 'Saved. Jobs that already have this checklist keep the version they started with.' : 'Saved.');
        } catch (err) {
            console.error('ChecklistTemplateEditor: save failed', err);
            if (isStaleTemplateError(err)) setRefused(true);
            else setError(err?.message || 'Could not save the checklist. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // After a refusal: keep my edits as a separate, new checklist.
    const saveMineAsNew = async () => {
        if (!validation.ok) { setShowErrors(true); scrollToFirstError(); return; }
        const name = suffixedName(draft.name, ' (my version)');
        const sent = { ...draft, name };
        setSaving(true);
        setError('');
        try {
            const res = await saveChecklistTemplate({ ...toSaveInput(sent), expectedVersion: 0 });
            loadedRef.current = true;
            setDraft(sent);
            setBase(sent);
            setTemplateId(res.templateId);
            setVersion(res.version);
            setRefused(false);
            setNotice(`Saved as a new checklist, "${name}". The other version was not changed.`);
        } catch (err) {
            console.error('ChecklistTemplateEditor: save as new failed', err);
            setError(err?.message || 'Could not save the checklist. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const reloadTheirs = () => {
        if (!latestRef.current) return;
        if (dirty && !window.confirm('This discards your changes and loads the version that was saved most recently.')) return;
        adopt(latestRef.current);
        setRefused(false);
        setShowErrors(false);
        setError('');
        setNotice('Loaded the latest version.');
    };

    // ── render ──────────────────────────────────────────────────────────────
    if (missing && !draft) {
        return (
            <div className="p-6 space-y-3">
                <p className="text-sm text-gray-700">This checklist no longer exists.</p>
                <button type="button" onClick={onExit} className="flex items-center gap-1 text-sm font-medium text-[#5F4EC9]">
                    <ChevronLeft size={16} /> All checklists
                </button>
            </div>
        );
    }
    if (!draft || version === null) {
        return <div className="p-6 text-sm text-gray-400">{error || 'Loading…'}</div>;
    }

    const conflictLive = !!templateId && liveVersion !== null && liveVersion > version && dirty && !saving;
    const showStale = refused || conflictLive;
    const errs = showErrors ? validation : null;
    const blockingWithoutRequired = draft.blocksCompletion && counts.required === 0;
    const saveDisabled = saving || refused || missing || (!!templateId && !dirty);

    return (
        <div ref={containerRef} className="p-4 sm:p-6 space-y-5">
            {/* Top bar */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-gray-100">
                <div className="min-w-0">
                    <button type="button" onClick={leave} className="flex items-center gap-0.5 text-sm font-medium text-[#5F4EC9] hover:underline">
                        <ChevronLeft size={16} /> All checklists
                    </button>
                    <h2 className="mt-1 text-lg font-bold text-gray-800 truncate">{draft.name.trim() || 'New checklist'}</h2>
                    <p className="text-xs text-gray-500">{questionSummary(sections)}{dirty ? ' · unsaved changes' : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                    <button type="button" onClick={leave} disabled={saving}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSave} disabled={saveDisabled}
                        className="px-4 py-2 text-sm font-semibold text-white bg-[#5F4EC9] rounded-lg hover:opacity-90 disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save checklist'}
                    </button>
                </div>
            </div>

            {missing && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" /><p>This checklist no longer exists, so it can't be saved.</p>
                </div>
            )}

            {showStale && (
                <div className="p-4 bg-red-50 border border-red-300 rounded-xl space-y-2">
                    <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><AlertTriangle size={16} /> {STALE_TEXT}</p>
                    <p className="text-sm text-red-800">
                        {refused
                            ? 'Your changes were NOT saved. They are still on screen.'
                            : 'Saving now would be refused. Your changes are still on screen.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={reloadTheirs} disabled={saving || !latestRef.current}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                            <RefreshCw size={14} /> Reload their version
                        </button>
                        <button type="button" onClick={saveMineAsNew} disabled={saving}
                            className="px-3 py-1.5 text-sm font-medium text-red-800 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50">
                            {saving ? 'Saving…' : 'Save mine as a new checklist'}
                        </button>
                    </div>
                </div>
            )}

            {notice && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800">
                    <CheckCircle size={16} className="shrink-0 mt-0.5" /><p>{notice}</p>
                </div>
            )}
            {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" /><p>{error}</p>
                </div>
            )}
            {errs && errs.form.length > 0 && (
                <div data-cl-error className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 space-y-0.5">
                    {errs.form.map((m) => <p key={m}>{m}</p>)}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* ── Right panel (stacks first on narrow screens) ── */}
                <div className="lg:col-span-1 lg:order-2 space-y-5">
                    <div className="p-4 bg-white border border-gray-200 rounded-xl space-y-5 lg:sticky lg:top-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-800 mb-1" htmlFor="cl-name">Checklist name</label>
                            <input id="cl-name" type="text" value={draft.name} maxLength={CHECKLIST_LIMITS.templateName}
                                placeholder="e.g. Truck check"
                                onChange={(e) => { const v = e.target.value; edit((d) => ({ ...d, name: v })); }}
                                className={`${INPUT} ${errs?.name ? 'border-red-400' : 'border-gray-200'}`} />
                            <FieldError messages={errs?.name ? [errs.name] : null} />
                        </div>

                        <fieldset>
                            <legend className="text-sm font-semibold text-gray-800 mb-2">Which jobs get it?</legend>
                            {[
                                ['every_job', 'Every new job', 'added automatically whenever a job is created'],
                                ['manual', 'Only jobs we add it to', 'the office or a tech adds it from the job'],
                            ].map(([value, title, hint]) => (
                                <label key={value} className="flex items-start gap-2 py-1.5 cursor-pointer">
                                    <input type="radio" name="cl-autoattach" value={value} checked={draft.autoAttach === value}
                                        onChange={() => edit((d) => ({ ...d, autoAttach: value }))}
                                        className="mt-0.5 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                                    <span className="text-sm text-gray-700"><span className="font-medium">{title}</span> — {hint}</span>
                                </label>
                            ))}
                            {draft.autoAttach === 'every_job' && (
                                <p className="mt-1 text-xs text-[#5F4EC9]">Added to new jobs from now on. Jobs that already exist aren't changed.</p>
                            )}
                        </fieldset>

                        <div>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <button type="button" role="switch" aria-checked={draft.blocksCompletion}
                                    onClick={() => edit((d) => ({ ...d, blocksCompletion: !d.blocksCompletion }))}
                                    className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${draft.blocksCompletion ? 'bg-[#5F4EC9]' : 'bg-gray-300'}`}>
                                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${draft.blocksCompletion ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                </button>
                                <span className="text-sm text-gray-700">
                                    <span className="font-medium flex items-start gap-1.5"><Lock size={13} className="shrink-0 mt-0.5" /><span>Must be finished before the job can be completed</span></span>
                                    <span className="block text-xs text-gray-500 mt-0.5">An Owner, Admin or Manager can still complete the job anyway by giving a reason.</span>
                                </span>
                            </label>
                            {blockingWithoutRequired && (
                                <div className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                    <p>Nothing on this checklist is required, so it won't stop a job from being completed. Mark at least one question Required.</p>
                                </div>
                            )}
                        </div>

                        {/* custcopy-32471cbd — ON for a new checklist; templates saved before Seam 8 open OFF */}
                        <div>
                            <label className="flex items-start gap-3 cursor-pointer">
                                <button type="button" role="switch" aria-checked={draft.customerCopy === true}
                                    onClick={() => edit((d) => ({ ...d, customerCopy: d.customerCopy !== true }))}
                                    className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${draft.customerCopy === true ? 'bg-[#5F4EC9]' : 'bg-gray-300'}`}>
                                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${draft.customerCopy === true ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                </button>
                                <span className="text-sm text-gray-700">
                                    <span className="font-medium">Customers can receive a copy</span>
                                    <span className="block text-xs text-gray-500 mt-0.5">
                                        {draft.customerCopy === true
                                            ? 'The office can email the customer a copy. Questions marked "Office only" are left out.'
                                            : 'Office only. The office can still print it, but it can\'t be sent to the customer.'}
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* ── Left: sections and questions ── */}
                <div className="lg:col-span-2 lg:order-1 space-y-4">
                    {sections.map((s, si) => {
                        const pickerOpen = pickerFor === s.id;
                        const dupBlocked = duplicateSectionBlocked(draft, s.id);
                        return (
                            <div key={s.id} className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#5F4EC9]">Section {si + 1}</p>
                                        <input type="text" value={s.title} maxLength={CHECKLIST_LIMITS.sectionTitle}
                                            placeholder="Section title (optional)" aria-label={`Section ${si + 1} title`}
                                            onChange={(e) => { const v = e.target.value; edit((d) => updateSection(d, s.id, { title: v })); }}
                                            className={`mt-0.5 w-full px-3 py-1.5 text-base font-bold text-gray-800 border rounded-lg bg-white ${errs?.bySection[s.id] ? 'border-red-400' : 'border-gray-200'}`} />
                                        <FieldError messages={errs?.bySection[s.id] ? [errs.bySection[s.id]] : null} />
                                    </div>
                                    <div className="mt-5 flex items-center gap-1 shrink-0">
                                        <button type="button" onClick={() => onDuplicateSection(s)} disabled={!!dupBlocked}
                                            title={dupBlocked || 'Copy this section and its questions, just below'}
                                            className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                                            <Copy size={13} /> Duplicate section
                                        </button>
                                        {sections.length > 1 && (
                                            <button type="button" onClick={() => onRemoveSection(s)} aria-label="Remove section" title="Remove section"
                                                className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash2 size={15} /></button>
                                        )}
                                    </div>
                                </div>
                                {dupBlocked && <p className="-mt-1 text-xs text-gray-500 text-right">Can't duplicate: {dupBlocked}</p>}

                                {/* The section's questions, framed and indented under its header */}
                                <div className="ml-2 pl-3 border-l-2 border-[#5F4EC9]/25 space-y-2">
                                {s.questions.length === 0 && (
                                    <p className="text-sm text-gray-400 py-1">No questions yet.</p>
                                )}

                                {s.questions.map((q) => {
                                    const Icon = TYPE_ICONS[q.type] || Info;
                                    const qErrs = errs?.byQuestion[q.id];
                                    const isInfo = q.type === 'info';
                                    return (
                                        <div key={q.id} id={`clq-${q.id}`} className={`p-3 bg-white border rounded-lg space-y-2 ${qErrs ? 'border-red-300' : 'border-gray-200'}`}>
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="flex items-center gap-1.5">
                                                    <Icon size={14} className="text-[#5F4EC9] shrink-0" />
                                                    <select value={q.type} aria-label="Question type"
                                                        onChange={(e) => onChangeType(q, e.target.value)}
                                                        className="px-1.5 py-0.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:border-[#5F4EC9]/40">
                                                        {QUESTION_TYPE_ORDER.map((t) => <option key={t} value={t}>{QUESTION_TYPE_LABELS[t]}</option>)}
                                                    </select>
                                                </span>
                                                <div className="flex flex-wrap items-center gap-1">
                                                    {sections.length > 1 && (
                                                        <select value="" aria-label="Move to section"
                                                            onChange={(e) => { const t = e.target.value; if (t) edit((d) => moveQuestionToSection(d, q.id, t)); }}
                                                            className="mr-1 max-w-[11rem] px-1.5 py-1 text-xs text-gray-600 bg-white border border-gray-200 rounded-lg">
                                                            <option value="">Move to section ▾</option>
                                                            {sections.map((t, ti) => (ti === si ? null : (
                                                                <option key={t.id} value={t.id}>{sectionLabel(t, ti)}</option>
                                                            )))}
                                                        </select>
                                                    )}
                                                    {!isInfo && (
                                                        <label className="flex items-center gap-1.5 mr-2 text-xs text-gray-600 cursor-pointer">
                                                            <input type="checkbox" checked={q.required}
                                                                onChange={(e) => { const v = e.target.checked; edit((d) => updateQuestion(d, q.id, { required: v })); }}
                                                                className="rounded border-gray-300 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                                                            Required
                                                        </label>
                                                    )}
                                                    {/* Only the usable direction shows; none for the only question in a section */}
                                                    {canMoveQuestion(draft, q.id, -1) && (
                                                        <button type="button" onClick={() => edit((d) => moveQuestion(d, q.id, -1))}
                                                            className="p-1 rounded hover:bg-gray-100" aria-label="Move up" title="Move up"><ArrowUp size={14} /></button>
                                                    )}
                                                    {canMoveQuestion(draft, q.id, 1) && (
                                                        <button type="button" onClick={() => edit((d) => moveQuestion(d, q.id, 1))}
                                                            className="p-1 rounded hover:bg-gray-100" aria-label="Move down" title="Move down"><ArrowDown size={14} /></button>
                                                    )}
                                                    <button type="button" onClick={() => edit((d) => removeQuestion(d, q.id))}
                                                        className="p-1 rounded text-red-500 hover:bg-red-50" aria-label="Delete question"><Trash2 size={14} /></button>
                                                </div>
                                            </div>

                                            {isInfo ? (
                                                <textarea rows={2} value={q.label} maxLength={CHECKLIST_LIMITS.questionLabel}
                                                    placeholder="Instructions for the tech (no answer needed)"
                                                    onChange={(e) => { const v = e.target.value; edit((d) => updateQuestion(d, q.id, { label: v })); }}
                                                    className={`${INPUT} border-gray-200`} />
                                            ) : (
                                                <input type="text" value={q.label} maxLength={CHECKLIST_LIMITS.questionLabel}
                                                    placeholder="Type the question"
                                                    onChange={(e) => { const v = e.target.value; edit((d) => updateQuestion(d, q.id, { label: v })); }}
                                                    className={`${INPUT} border-gray-200`} />
                                            )}

                                            {OPTION_TYPES.includes(q.type) && (
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">Choices, separated by commas</label>
                                                    <input type="text" value={q.optionsText} placeholder="Pass, Fail, Not checked"
                                                        onChange={(e) => { const v = e.target.value; edit((d) => updateQuestion(d, q.id, { optionsText: v })); }}
                                                        className={`${INPUT} border-gray-200`} />
                                                </div>
                                            )}
                                            {q.type === 'number' && (
                                                <div className="flex items-center gap-2">
                                                    <label className="text-xs text-gray-500" htmlFor={`clu-${q.id}`}>Unit (optional)</label>
                                                    <input id={`clu-${q.id}`} type="text" value={q.unit} maxLength={CHECKLIST_LIMITS.unit} placeholder="m, kg, psi"
                                                        onChange={(e) => { const v = e.target.value; edit((d) => updateQuestion(d, q.id, { unit: v })); }}
                                                        className="w-28 px-2 py-1 text-sm border border-gray-200 rounded-lg" />
                                                </div>
                                            )}
                                            {q.type === 'yesno' && (
                                                <div className="flex gap-1.5" aria-hidden="true">
                                                    {['Yes', 'No', 'N/A'].map((l) => (
                                                        <span key={l} className="px-3 py-1 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-full">{l}</span>
                                                    ))}
                                                </div>
                                            )}
                                            {/* chklstmulti-3e9b7c15 — per-question switches (note/photos never on Instructions);
                                                custcopy-32471cbd — "Office only" on every type */}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
                                                {canAllowNote(q.type) && (
                                                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" title="The tech can type a note under their answer">
                                                        <input type="checkbox" checked={q.allowNote === true}
                                                            onChange={(e) => { const v = e.target.checked; edit((d) => updateQuestion(d, q.id, { allowNote: v })); }}
                                                            className="rounded border-gray-300 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                                                        Allow a note
                                                    </label>
                                                )}
                                                {canAllowPhotos(q.type) && (
                                                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" title="The tech can attach up to 10 photos to this answer">
                                                        <input type="checkbox" checked={q.allowPhotos === true}
                                                            onChange={(e) => { const v = e.target.checked; edit((d) => updateQuestion(d, q.id, { allowPhotos: v })); }}
                                                            className="rounded border-gray-300 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                                                        Allow photos
                                                    </label>
                                                )}
                                                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" title="Left out of the copy the customer receives">
                                                    <input type="checkbox" checked={q.officeOnly === true}
                                                        onChange={(e) => { const v = e.target.checked; edit((d) => updateQuestion(d, q.id, { officeOnly: v })); }}
                                                        className="rounded border-gray-300 text-[#5F4EC9] focus:ring-[#5F4EC9]" />
                                                    Office only — hide from the customer copy
                                                </label>
                                            </div>
                                            <FieldError messages={qErrs} />
                                        </div>
                                    );
                                })}

                                {/* + Add question: the 10 types open right here; the pick goes at the END of this section */}
                                {pickerOpen ? (
                                    <div className="p-3 bg-white border border-[#5F4EC9]/40 rounded-lg">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs font-semibold text-gray-700">Pick a question type</p>
                                            <button type="button" onClick={() => setPickerFor(null)} aria-label="Close"
                                                className="p-1 rounded text-gray-400 hover:bg-gray-100"><X size={14} /></button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                            {QUESTION_TYPE_ORDER.map((type) => {
                                                const Icon = TYPE_ICONS[type];
                                                return (
                                                    <button key={type} type="button" onClick={() => onAddQuestion(s.id, type)}
                                                        className="flex items-start gap-2.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-[#5F4EC9]/5 hover:border-[#5F4EC9]/40 text-left min-w-0">
                                                        <Icon size={16} className="text-[#5F4EC9] shrink-0 mt-0.5" />
                                                        <span className="min-w-0">
                                                            <span className="block text-sm font-medium text-gray-800 whitespace-nowrap">{QUESTION_TYPE_LABELS[type]}</span>
                                                            <span className="block text-xs text-gray-500">{QUESTION_TYPE_HINTS[type]}</span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <button type="button" onClick={() => setPickerFor(s.id)} disabled={atQuestionCap}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[#5F4EC9] bg-white border border-[#5F4EC9]/40 rounded-lg hover:bg-[#5F4EC9]/5 disabled:opacity-40">
                                        <Plus size={14} /> Add question
                                    </button>
                                )}
                                {atQuestionCap && <p className="text-xs text-gray-500">A checklist can have at most {CHECKLIST_LIMITS.questions} questions.</p>}
                                </div>
                            </div>
                        );
                    })}

                    <button type="button" onClick={onAddSection} disabled={atSectionCap}
                        className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                        <Plus size={14} /> Add section
                    </button>
                    {atSectionCap && <p className="text-xs text-gray-500">A checklist can have at most {CHECKLIST_LIMITS.sections} sections.</p>}
                </div>
            </div>
        </div>
    );
}
